import {List} from 'immutable';
import _ from 'lodash';

import t from '../lib/I18n';
import {
    AccountPages,
    ActionTypes,
    filteredRailcards,
    GlobalModals,
    LoadingTypes,
    RAILCARD1617_CODE,
    Railcard1617Errors,
    SmartcardStatus,
    RegistrationReferrers,
    PaymentAuthCodes,
    STicketStatus
} from '../constants/AppConstants';

import FeatureFlagStore from '../stores/FeatureFlagStore';
import SmartcardStore from '../stores/SmartcardStore';
import UserStore from '../stores/UserStore';

import AppDispatcher from '../dispatcher/AppDispatcher';

import AddressService from '../services/AddressService';
import CustomerService from '../services/CustomerService';
import KeyGoService from '../services/KeyGoService';
import OrderHistoryService from '../services/OrderHistoryService';
import PaymentCardService from '../services/PaymentCardService';
import PhotocardService from '../services/PhotocardService';
import RailcardsService from '../services/RailcardsService';
import SmartcardService from '../services/SmartcardService';
import VoucherService from '../services/VoucherService';
import STicketService from '../services/STicketService';

import ConfirmationModalActions from './ConfirmationModalActions';
import TokenActions from './TokenActions';

import urls from '../urls';
import trainTime from '../lib/TrainTime';

import { AccountRailcardSubmissionError } from '../errors/AccountRailcard';
import {
    CustomerEmailInUseError,
    CustomerRegistrationError,
    CustomerLoginError,
    CustomerRefreshError,
    CustomerMaxFailedLoginAttemptsReached,
    CustomerAccountDormant,
    CustomerAccountLocked,
} from '../errors/Customer';
import { SmartcardRegistrationError, SmartcardActivationError, SelfSmartcardRegistrationError } from '../errors/Smartcard';

import { createLoadingDecoratorFor } from '../decorators/Loading';

import RegistrationForm from '../records/customer/Registration';
import UserPreferences from "../records/customer/UserPreferences";
import EditPreferencesForm from '../records/customer/EditPreferencesForm';
import SmartcardAssociationInfo from '../records/customer/SmartcardAssociationInfo';
import { resolve } from '../helpers/LinkMapper';
import AuthResponse from '../records/payment/AuthResponse';
import ModalActions from './ModalActions';

const withSmartcardLoadingActions = createLoadingDecoratorFor(LoadingTypes.SMARTCARD);
const isErrorPresent = (error) => error && error.body && error.body.errors && error.body.errors.length;

const CustomerActions = {

    registerWithPayment(registration, payment) {
        let form = (new RegistrationForm())
            .merge(registration.toJS())
            .set('email', payment.contact.email)
            .set('referrer', RegistrationReferrers.PAYMENT_FORM);

        if (!registration.get('phone')) {
            form = form.set('phone', payment.contact.phone);
        }

        return CustomerActions.register(form);
    },

    registerWithDelayRepay(form) {
        form = (new RegistrationForm(form)).set('referrer', RegistrationReferrers.DELAY_REPAY);
        return this.register(form);
    },

    async register (registration) {
        try {
            AppDispatcher.dispatch({
                actionType: ActionTypes.REGISTRATION_STARTED,
            });

            const user = await CustomerService.create(registration);

            AppDispatcher.dispatch({
                actionType: ActionTypes.REGISTRATION_SUCCESS,
                user: user
            });

            AppDispatcher.dispatch({
                actionType: ActionTypes.LOGGED_IN,
                user
            });

            TokenActions.updateDevice(user.accessTokenDevice);

            return user;
        } catch (error) {
            console.error(error);
            const errors = _.result(error, 'body.errors', []);
            const emailIsInUse = _.find(errors, { code: 8000 });
            const migrationRequired = _.find(errors, { code: 8100 });

            AppDispatcher.dispatch({
                actionType: ActionTypes.REGISTRATION_FAILED,
            });

            if (migrationRequired || emailIsInUse) { throw new CustomerEmailInUseError(); }
            throw new CustomerRegistrationError('Unexpected registration error', { error });
        }
    },

    async loginWithToken({ token }) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.AUTH_LOADED,
            token
        });

        const user = await CustomerService.fetch(token);

        AppDispatcher.dispatch({
            actionType: ActionTypes.LOGGED_IN,
            user
        });

        this.notifyApprovedRailcards({ onlyUnseen: true });

        return user;
    },

    async login ({ username, password }) {
        try {
            const token = await CustomerService.auth(username, password);

            return CustomerActions.loginWithToken({ token });
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.AUTH_ERROR,
                error: error
            });
            console.error(error);
            if (error.hasCode && error.hasCode(CustomerMaxFailedLoginAttemptsReached.code)) {
                throw new CustomerMaxFailedLoginAttemptsReached();
            } else if (error.hasCode && error.hasCode(CustomerAccountDormant.code)) {
                throw new CustomerAccountDormant();
            } else if (error.hasCode && error.hasCode(CustomerAccountLocked.code)) {
                throw new CustomerAccountLocked();
            }
            throw new CustomerLoginError();
        }
    },

    async setPreferredBrand (user, brandName) {
        const brandUpdate = await CustomerService.update(user, { brandUpdate: brandName });
        if (user.isLoggedIn() && brandUpdate) {
            user = brandUpdate;
        } else {
            user = await CustomerService.fetch(user.authToken, user);
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_UPDATED,
            user
        });

        return user;
    },

    async logout (user) {
        if (user && user.isLoggedIn()) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.LOGGED_OUT
            });
            CustomerService.logout(user);
        }
    },

    async resetPassword (resetToken, password) {
        const authToken = await CustomerService.resetPassword(resetToken, password);

        if (authToken) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.AUTH_LOADED,
                token: authToken
            });

            const user = await CustomerService.fetch(authToken);

            AppDispatcher.dispatch({
                actionType: ActionTypes.LOGGED_IN,
                user
            });

            return user;
        }
        return null;
    },

    async refresh (user, shouldRefreshTokens = true) {
        const isRefreshingAuthToken = UserStore.getIsRefreshingAuthToken();

        /*
            if we try to refresh an auth token in the middle of a previous
            refresh request, we get an "Invalid refresh token" error back from
            the customers/auth/refresh endpoint because it tries to use a token
            that has now been deleted from the DB
        */
        if (isRefreshingAuthToken) {
            return false;
        }

        try {
            let { accessToken: token } = user.customer;

            if (shouldRefreshTokens) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AUTH_REFRESH_STARTED,
                    token
                });
                token = await CustomerService.refresh(user);
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AUTH_REFRESHED,
                    token
                });
            }

            if (token) {
                const fetchedUser = await CustomerService.fetch(token);

                AppDispatcher.dispatch({
                    actionType: ActionTypes.USER_UPDATED,
                    user: fetchedUser
                });

                await CustomerActions.getSmartcard(fetchedUser);
                return fetchedUser;
            }
        } catch (error) {
            console.error(error);
            AppDispatcher.dispatch({
                actionType: ActionTypes.LOGGED_OUT,
            });
            throw new CustomerRefreshError();
        }
        return null;
    },

    async fetchUser (token) {
        const user = await CustomerService.fetch(token);

        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_UPDATED,
            user
        });

        return user;
    },

    async updateAuthenticatedProperties ({ user, currentPassword, changes, requireAuth = true }) {
        try {
            if (requireAuth) {
                await CustomerService.auth(user.email, currentPassword);
            }
        } catch (error) {
            console.error(error);
            throw new CustomerLoginError();
        }

        const updated = await CustomerService.update(user, changes);
        if (updated) {
            user = updated;
        } else {
            user = await CustomerService.fetch(user.authToken, user);
        }
        if (changes.password) {
            user = user.setAuthToken(user.authToken.set('showPasswordChangeModal', false));
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_UPDATED,
            user,
        });

        return user;
    },

    async checkEmail (email) {
        const checkEmail = await CustomerService.checkEmailAddress(email);
        AppDispatcher.dispatch({
            actionType: ActionTypes.CHECK_EMAIL,
            email,
        });
        return checkEmail;
    },

    async updatePreferences (userId, form, omitAge = false) {
        if (FeatureFlagStore.get('enableCACIv2')) {
            await CustomerService.updatePreferencesV2(userId, form);
        }
        const preferences = await CustomerService.updatePreferences(userId, form, omitAge);
        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_PREFERENCES_UPDATED,
            preferences,
            isOver16: form.get('isOver16'),
            telephoneNumber: form.get('telephoneNumber')
        });
        return preferences;
    },

    async updateName (user, form) {
        const newUser = await CustomerService.update(user, form);

        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_UPDATED,
            user: newUser,
        });

        return newUser;
    },

    async fetchPreferences (userId) {
        const preferences = await CustomerService.fetchPreferences(userId);
        AppDispatcher.dispatch({
            actionType: ActionTypes.USER_PREFERENCES_FETCHED,
            preferences
        });
        return preferences;
    },

    async getEmailUserPreferences (userId, token) {
        return await CustomerService.fetchEmailUserPreferences(userId, token);
    },

    async updateEmailUserPreferences (userId, token, preferences) {
        await CustomerService.updateEmailUserPreferences(userId, token, preferences);
        return Promise.resolve();
    },

    async setPasswordAndPreferences(token, form) {
        try {
            const authToken = await CustomerService.resetPassword(token, form.password);

            if (authToken) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.AUTH_LOADED,
                    token: authToken
                });

                const user = await CustomerService.fetch(authToken);

                AppDispatcher.dispatch({
                    actionType: ActionTypes.LOGGED_IN,
                    user
                });

                if (user) {
                    const isOver16 = form.get('isOver16') || user.isOver16;
                    const telephoneNumber = form.get('telephoneNumber') || user.get('telephoneNumber');

                    const preferences = await CustomerService.patchPreferences(
                        user.id,
                        UserPreferences.getRegistrationPreferencesFields(form.get('preferences')),
                    );
                    AppDispatcher.dispatch({
                        actionType: ActionTypes.USER_PREFERENCES_UPDATED,
                        preferences,
                        isOver16,
                        telephoneNumber,
                    });
                }

                return user;
            }
            return null;
        } catch (error) {
            throw error;
        }
    },

    async getVouchers (user) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_VOUCHERS_STARTED
        });

        try {
            const vouchers = await VoucherService.getAll(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.CUSTOMER_VOUCHERS_LOADED,
                vouchers: vouchers
            });
            return vouchers;
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.CUSTOMER_VOUCHERS_FAILED,
                error: error
            });
            return null;
        }
    },

    async getPaymentCards (user) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_PAYMENTCARDS_STARTED
        });

        const cards = await PaymentCardService.getAll(user);

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_PAYMENTCARDS_LOADED,
            cards
        });

        return cards;
    },

    async deletePaymentCard (user, card) {
        await PaymentCardService.delete(user, card);

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_PAYMENTCARD_DELETED,
            deleted: card
        });
    },

    async getBraintreePaymentCards (user) {
        const cards = await PaymentCardService.getBraintreeAll(user);

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_BRAINTREE_PAYMENTCARDS_LOADED,
            cards
        });

        return cards;
    },

    async deleteBraintreePaymentCard (user, card) {
        await PaymentCardService.deleteBraintree(user, card);

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_BRAINTREE_PAYMENTCARD_DELETED,
            deleted: card
        });
    },


    @withSmartcardLoadingActions
    async registerSmartcard (user, form) {
        try {
            await SmartcardService.registerSmartcard(user, form);
            user = user.setHasRegisteredSmartcard(true);

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_REGISTERED
            });

            if (FeatureFlagStore.get('enableAutoDelayRepay')) {
                const currentPreferences = EditPreferencesForm.fromUser(user);

                const updatedPreferences = await this.fetchPreferences(user.id);

                /*
                    The below must be set to false for under 16s.
                    When registering for a Child Smartcard, the BE will
                    override any marketing prefs to false, and the FE must get these
                    to avoid accidentally setting these back to true when opting in to
                    auto delay repay

                    emailPromotions
                    smsPromotions
                    emailProducts
                    smsProducts
                */

                const {
                    emailPromotions,
                    smsPromotions,
                    emailProducts,
                    smsProducts
                } = updatedPreferences;

                const finalPreferences = currentPreferences
                    .setIn(['preferences', 'autoDelayRepay'], true) // opt the customer in to auto-delay-repay regardless of age
                    .setIn(['preferences', 'emailPromotions'], emailPromotions)
                    .setIn(['preferences', 'smsPromotions'], smsPromotions)
                    .setIn(['preferences', 'emailProducts'], emailProducts)
                    .setIn(['preferences', 'smsProducts'], smsProducts);

                await this.updatePreferences(user.id, finalPreferences, true);
            }

            //Load the users addresses, as we would have just added one
            await this.loadAddresses(user);

            return await this.getSmartcard(user);

        } catch (error) {
            if (!(error instanceof SmartcardRegistrationError)) {
                console.error(error);
                throw new SmartcardRegistrationError('Unexpected registration failure', { error });
            }
            throw error;
        }
    },

    @withSmartcardLoadingActions
    async linkSmartcard (user, form, type) {
        try {
            await SmartcardService.linkSmartcard(user, form, type);
            user = user.setHasRegisteredSmartcard(true);

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_LINKED
            });

            if (FeatureFlagStore.get('enableAutoDelayRepay')) {
                const updatedPreferences = EditPreferencesForm.fromUser(user).setIn(['preferences', 'autoDelayRepay'], true);
                await this.updatePreferences(user.id, updatedPreferences);
            }

            //Load the users addresses, as we would have just added one
            await this.loadAddresses(user);

            return await this.getSmartcard(user);

        } catch (error) {
            if (!(error instanceof SelfSmartcardRegistrationError)) {
                console.error(error);
                throw new SelfSmartcardRegistrationError('Unexpected registration failure', { error });
            }
            throw error;
        }
    },

    async getSmartcard (user, enableCache = true) {
        if (!FeatureFlagStore.get('enableSmartcard')) {
            return Promise.resolve(null);
        }

        try {
            const smartcard = await SmartcardService.getSmartcard(user, enableCache);
            if (smartcard) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMARTCARD_LOADED,
                    smartcard: smartcard
                });
            }
            return smartcard;
        } catch (error) {
            console.error(error);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_FAILED,
                error
            });

            if (!SmartcardStore.get('smartcardFailureNotified')) {
                AppDispatcher.dispatch({
                    actionType: ActionTypes.MODAL_OPENED,
                    type: GlobalModals.ERROR,
                    data: {
                        heading: 'error.smartcard.heading',
                        title: 'error.smartcard.title',
                        message: 'error.smartcard.message'
                    }
                });
                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMARTCARD_FAILURE_NOTIFIED
                });
            }
            return null;
        }
    },

    async getSmartcardPhoto (user) {
        if (!FeatureFlagStore.get('enableSmartcard')) {
            return Promise.resolve(null);
        }
        return await SmartcardService.getSmartcardPhoto(user);
    },

    async uploadNewSmartcardPhoto (user, photo) {
        return await SmartcardService.uploadNewSmartcardPhoto(user, photo);
    },

    disableCollectionLocation() {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_SET_SHOW_COLLECTION_LOCATION,
            showCollectionLocation: false
        }));
    },

    async refreshSmartcard (user, delay = 5000) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_DIRTY
        });

        // Wait using a plain setTimeout wrapped in a Promise
        // eslint-disable-next-line no-shadow
        await new Promise((resolve) => {
            setTimeout(resolve, delay);
        });

        return CustomerActions.getSmartcard(user);
    },

    async getAllSmartcards (user) {
        const smartcards = await SmartcardService.getAllSmartcards(user);
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARDS_LOADED,
            smartcards
        });
        return smartcards;
    },

    getSmartcardUrl (user) {
        return SmartcardService.getSmartcardUrl(user);
    },

    async getOrderHistory (user, params = {}) {
        if (!user || !user.isLoggedIn()) { return; }

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_ORDERS_SEARCHING,
            params
        });

        try {
            const { orders, pagination } = await OrderHistoryService.search(user, params);

            AppDispatcher.dispatch({
                actionType: ActionTypes.CUSTOMER_ORDERS_LOADED,
                orders, pagination
            });
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.CUSTOMER_ORDERS_FAILED,
                error
            });
        }
    },

    async getOrderDetails (orderId) {
        const order = await OrderHistoryService.fetch(orderId);
        let refundCalculations = new List();
        if (order.hasRefunds()) {
            refundCalculations = await OrderHistoryService.fetchRefundCalculations(orderId);
        }
        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_ORDER_LOADED,
            order,
            refundCalculations
        });

        return order;
    },

    async getOrderHistoryDetails (orderId) {
        const order = await OrderHistoryService.getOrderHistory(orderId);

        AppDispatcher.dispatch({
            actionType: ActionTypes.CUSTOMER_ORDER_LOADED,
            order
        });

        return order;
    },

    async getKeyGoTaps (user, smartcard) {
        if (!smartcard || !smartcard.hasKeyGo()) { return; }

        const taps = await SmartcardService.getKeyGoTaps(user, smartcard);
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_TAPS_LOADED,
            taps
        });
    },

    async getKeyGoTransactionHistory (smartcard, queryParams = {}) {
        if (!smartcard) { return; }

        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_TRANSACTIONS_LOADING
        });

        const transactionHistory = await KeyGoService.getKeyGoTransactions(queryParams);
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_TRANSACTIONS_LOADED,
            transactionHistory: transactionHistory
        });
    },

    async getSmartcardJourneys (user, smartcard) {
        if (!smartcard || smartcard.hasKeyGo()) { return; }

        const journeys = await SmartcardService.getJourneys(user, smartcard);
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_JOURNEYS_LOADED,
            journeys
        });
    },

    resetKeyGoForm () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_FORM_RESET
        });
    },

    changeKeyGoForm (field, value) {
        AppDispatcher.dispatch({
            field,
            value,
            actionType: ActionTypes.KEYGO_FORM_CHANGED
        });
    },

    changeRailcard1617Form (field, value) {
        AppDispatcher.dispatch({
            field,
            value,
            actionType: ActionTypes.RAILCARD1617_FORM_CHANGED
        });
    },

    keyGoFormAddressSelected (address) {
        AppDispatcher.dispatch({
            address,
            actionType: ActionTypes.KEYGO_ADDRESS_SELECTED
        });
    },

    retryKeyGoForm () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_FORM_RETRY
        });
    },

    registrationKeyGoFormComplete () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_REGISTRATION_COMPLETE
        });
    },

    keyGoUpdateComplete () {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_UPDATE_COMPLETE
        });
    },

    async registerKeyGo (user, form) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_REGISTRATION_STARTED,
            collectionLocation: form.collectionLocation
        });

        try {
            const response = await SmartcardService.registerKeyGo(user, form);
            const threeDSecureAuthResponse = resolve(response.result).as(AuthResponse);

            if (FeatureFlagStore.get('enableRailcard1617')) {
                this.notifyApprovedRailcards({ filterRailcards: ['TSU'], goToRailcardsPageOnConfirm: false });
            }

            if (threeDSecureAuthResponse.isEnrolled()) {
                return threeDSecureAuthResponse;
            } else if (!threeDSecureAuthResponse.isEnrolled() && !threeDSecureAuthResponse.isError()) {
                CustomerActions.registrationKeyGoFormComplete();
                CustomerActions.getPaymentCards(user);
                return threeDSecureAuthResponse;
            } else { // assumed to be an error scenario
                const { code } = threeDSecureAuthResponse;
                const messages = _.invert(PaymentAuthCodes);
                const message = messages[code] || 'unknown error';

                AppDispatcher.dispatch({
                    message,
                    actionType: ActionTypes.KEYGO_REGISTRATION_FAILED
                });
            }
        } catch (error) {
            AppDispatcher.dispatch({
                error,
                actionType: ActionTypes.KEYGO_REGISTRATION_FAILED
            });
        }

        return undefined;
    },

    async registerKeyGoResume (user, paymentId) {
        const { result } = await SmartcardService.keyGoResume(user.customer.id, paymentId);
        const { status } = result;

        if (status === 'success') {
            CustomerActions.registrationKeyGoFormComplete();
            CustomerActions.getPaymentCards(user);
        } else {
            AppDispatcher.dispatch({
                error: 'authorisation error',
                actionType: ActionTypes.KEYGO_UPDATE_FAILED
            });
        }

        return result;
    },

    async updateKeyGo (user, form) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_UPDATE_STARTED
        });

        try {
            const response = await SmartcardService.updateKeyGo(user, form);
            const threeDSecureAuthResponse = resolve(response.result).as(AuthResponse);

            if (threeDSecureAuthResponse.isEnrolled()) {
                return threeDSecureAuthResponse;
            } else if (!threeDSecureAuthResponse.isEnrolled() && !threeDSecureAuthResponse.isError()) {
                CustomerActions.keyGoUpdateComplete();
                CustomerActions.getPaymentCards(user);
                return threeDSecureAuthResponse;
            } else { // assumed to be an error scenario
                const { code } = threeDSecureAuthResponse;
                const messages = _.invert(PaymentAuthCodes);
                const message = messages[code] || 'unknown error';

                AppDispatcher.dispatch({
                    error: message,
                    actionType: ActionTypes.KEYGO_UPDATE_FAILED
                });
            }
        } catch (error) {
            AppDispatcher.dispatch({
                error,
                actionType: ActionTypes.KEYGO_UPDATE_FAILED
            });
        }

        return undefined;
    },

    async updateKeyGoResume (user, paymentId) {
        const { result } = await SmartcardService.keyGoResume(user.customer.id, paymentId);
        const { status } = result;

        if (status === 'success') {
            CustomerActions.keyGoUpdateComplete();
            CustomerActions.getPaymentCards(user);
        } else {
            AppDispatcher.dispatch({
                error: 'authorisation error',
                actionType: ActionTypes.KEYGO_UPDATE_FAILED
            });
        }

        return result;
    },

    async cancelKeyGo (user) {
        await SmartcardService.cancelKeyGo(user);
        AppDispatcher.dispatch({
            actionType: ActionTypes.KEYGO_CANCELLED
        });
    },

    async withdrawKeyGoBalance () {
        return await KeyGoService.withdrawKeyGoBalance();
    },

    async withdrawKeyGoBalanceWithCheque (reimbursementId) {
        return await KeyGoService.withdrawKeyGoBalanceWithCheque(reimbursementId);
    },

    async fetchChildSmartcardReplacement (user) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.FETCH_CHILD_SMARTCARD_REPLACEMENT_STARTED,
            userId: user && user.id,
        }));
        try {
            const smartcardConfirmation = await SmartcardService.fetchChildSmartcardReplacement(user);
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.FETCH_CHILD_SMARTCARD_REPLACEMENT_SUCCESS,
                smartcardConfirmation
            }));
            return smartcardConfirmation;
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.FETCH_CHILD_SMARTCARD_REPLACEMENT_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.confirm-smartcard.fetch-smartcard-confirmation.failed'});
            return null;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.FETCH_CHILD_SMARTCARD_REPLACEMENT_FINISHED
            }));
        }
    },

    async getSmartcardReplacementRequestSummary (user) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_REPLACEMENT_REQUEST_SUMMARY_STARTED,
            userId: user && user.id,
        });

        try {
            const requestSummary = await SmartcardService.getSmartcardReplacementRequestSummary(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_REPLACEMENT_REQUEST_SUMMARY_LOADED,
                requestSummary
            });
            return requestSummary;
        } catch (error) {
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_REPLACEMENT_REQUEST_SUMMARY_FAILED,
                error
            });
            ModalActions.open(GlobalModals.ERROR, { message: 'account.confirm-expiring-smartcard.request-smartcard-confirmation.failed' });
            return null;
        }
    },

    async requestChildSmartcardReplacement ({ user, isCardReplacementRequested }) {
        _.defer(() => AppDispatcher.dispatch({
            actionType: ActionTypes.REQUEST_CHILD_SMARTCARD_REPLACEMENT_STARTED,
            userId: user && user.id,
        }));
        try {
            await SmartcardService.requestChildSmartcardReplacement({
                user,
                isCardReplacementRequested,
            });
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.REQUEST_CHILD_SMARTCARD_REPLACEMENT_SUCCESS,
            }));
            return true;
        } catch (error) {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.REQUEST_CHILD_SMARTCARD_REPLACEMENT_FAILED,
                error
            }));
            ModalActions.open(GlobalModals.ERROR, { message: 'account.confirm-smartcard.request-smartcard-confirmation.failed'});
            return false;
        } finally {
            _.defer(() => AppDispatcher.dispatch({
                actionType: ActionTypes.REQUEST_CHILD_SMARTCARD_REPLACEMENT_FINISHED
            }));
        }
    },

    async replaceSmartcard (user, smartcardReplacement) {
        const { reason, additionalDetails, crimeNumber } = smartcardReplacement.toJS();
        const response = await SmartcardService.replaceSmartcard(user, { reason, additionalDetails, crimeNumber});
        AppDispatcher.dispatch({
            actionType: ActionTypes.REPLACEMENT_SMARTCARD_REQUESTED
        });
        return response;
    },

    async loadAddresses (user) {
        try {
            const addresses = await AddressService.getAll(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.ADDRESSES_LOADED,
                addresses
            });
        } catch (e) {
            console.warn('error getting customer addresses', e);
        }
    },

    async createAddress (user, address, isDefault) {
        const newAddress = await AddressService.create(user, address, isDefault);
        AppDispatcher.dispatch({
            actionType: ActionTypes.ADDRESS_CREATED,
            address: newAddress
        });
    },

    async deleteAddress (user, address) {
        await AddressService.delete(user, address);
        AppDispatcher.dispatch({
            actionType: ActionTypes.ADDRESS_DELETED,
            address
        });
    },

    async updateAddress (user, address, isDefault) {
        const updatedAddress = await AddressService.update(user, address, isDefault);
        AppDispatcher.dispatch({
            actionType: ActionTypes.ADDRESS_UPDATED,
            updatedAddress
        });
    },

    async loadPhotocards (user) {
        const photocards = await PhotocardService.getAll(user);
        AppDispatcher.dispatch({
            actionType: ActionTypes.PHOTOCARDS_LOADED,
            photocards
        });
        return photocards;
    },

    async resetPhotocards () {
        const photocards = new List();
        AppDispatcher.dispatch({
            actionType: ActionTypes.PHOTOCARDS_LOADED,
            photocards
        });
        return photocards;
    },

    async createPhotocard (user, photocard) {
        const newPhotocard = await PhotocardService.create(user, photocard);
        AppDispatcher.dispatch({
            actionType: ActionTypes.PHOTOCARD_CREATED,
            photocard: newPhotocard
        });
        return newPhotocard;
    },

    async updateSTicketCard(user, photocard) {
        return PhotocardService.updateSTicketCard(user, photocard);
    },

    async deletePhotocard (user, photocard) {
        await PhotocardService.delete(user, photocard);
        AppDispatcher.dispatch({
            actionType: ActionTypes.PHOTOCARD_DELETED,
            photocard
        });
    },

    async loadAccountRailcards (user) {
        if (!SmartcardStore.getCurrent()) {
            const smartcard = await this.getSmartcard(user);
            if (smartcard) {
                await this.getAllSmartcards(user);
            }
        }

        let filterRailcardCodes;

        if (FeatureFlagStore.get('enableRailcard1617')) {
            filterRailcardCodes = filteredRailcards.season;
        }

        const smartcard = SmartcardStore.getCurrent();
        const isKeyGo = !!(smartcard && smartcard.getKeyGoProduct());
        if (FeatureFlagStore.get('enableKeyGoRailcards') && isKeyGo) {
            filterRailcardCodes = filteredRailcards.keyGo;
        }

        const accountRailcards = await RailcardsService.getAccountRailcards(user, filterRailcardCodes);
        AppDispatcher.dispatch({
            actionType: ActionTypes.ACCOUNT_RAILCARDS_LOADED,
            accountRailcards
        });
        return accountRailcards;
    },

    async loadSTickets(user) {
        const sTickets = await STicketService.loadSTickets(user.id);
        return sTickets;
    },

    async loadSTicketSurfacedCoupons(user, query) {
        const sTicketsCoupons = await STicketService.getAllSurfacedCoupons(user.id, query);
        return sTicketsCoupons;
    },

    async hasAnyActiveSTicket(user) {
        const sTickets = await this.loadSTickets(user);
        return sTickets.some(({ status, isFlexi }) => status === STicketStatus.ACTIVE && !isFlexi);
    },

    async hasAnyPendingSTicketPhotoRequests(user) {
        const { result } = await STicketService.loadSTicketPhotoRequests(user);
        return result && !!result.length;
    },

    async submitRailcardApplication (user, data) {
        const formData = {
            firstName: data.get('firstName'),
            lastName: data.get('lastName'),
            railcardCode: data.get('railcardCode'),
            railcardNumber: data.get('railcardNumber'),
            expiryDate: trainTime.isoDate(data.get('expiryDate')),
            syncWithKeygo: data.syncWithKeygo,
        };
        const files = {
            photo: data.get('photo').get('selectedImage')
        };

        await RailcardsService.submitRailcardApplication(user, formData, files)
            .then((response) => {
                if (response.result && response.result.state === 'PENDING') {
                    AppDispatcher.dispatch({ actionType: ActionTypes.RAILCARD_APPLICATION_SUBMITTED });
                    AppDispatcher.dispatch({
                        actionType: ActionTypes.MODAL_OPENED,
                        type: GlobalModals.INFO,
                        data: {
                            message: 'notifications.railcard.applicationSubmitted'
                        }
                    });
                }
            })
            .catch((error) => {
                console.error(error);
                const hasErrors = isErrorPresent(error);
                const isRailcardAlreadyExistError = hasErrors && error.body.errors[0].message.includes(Railcard1617Errors.ALREADY_EXISTS);
                const isRailcardNotFound = hasErrors && error.body.errors[0].message.toLowerCase().includes('not be found');
                let heading, message, title = 'error.submit-railcard-application.message';

                if (isRailcardAlreadyExistError) {
                    heading = 'error.railcardAlreadyExist.heading';
                    message = 'error.railcardAlreadyExist.message';
                    title = 'error.railcardAlreadyExist.title';
                } else if (isRailcardNotFound) {
                    heading = 'error.railcardAlreadyExist.heading';
                    message = 'booking.railcard1617.errors.invalid';
                    title = 'error.railcardNotFound.title';
                }

                AppDispatcher.dispatch({
                    actionType: ActionTypes.MODAL_OPENED,
                    type: GlobalModals.ERROR,
                    data: {
                        heading,
                        message,
                        title
                    }
                });

                if (!isRailcardAlreadyExistError && !isRailcardNotFound) {
                    throw new AccountRailcardSubmissionError('Unexpected Railcard application submission error', { error });
                }
            });
    },

    async deleteAccountRailcard (user, accountRailcard) {
        await RailcardsService.deleteAccountRailcard(user, accountRailcard)
                .then(() => {
                    AppDispatcher.dispatch({
                        actionType: ActionTypes.ACCOUNT_RAILCARD_DELETED,
                        accountRailcard
                    });
                    AppDispatcher.dispatch({
                        actionType: ActionTypes.MODAL_OPENED,
                        type: GlobalModals.INFO,
                        data: {
                            message: 'notifications.railcard.deleted'
                        }
                    });
                })
                .catch(() => {
                    AppDispatcher.dispatch({
                        actionType: ActionTypes.MODAL_OPENED,
                        type: GlobalModals.ERROR,
                        data: {
                            message: 'error.remove-railcard.message'
                        }
                    });
                });
    },

    async notifyApprovedRailcards({ filterRailcards = [], goToRailcardsPageOnConfirm = true, onlyUnseen = false }) {
        if (FeatureFlagStore.get('enableAccountRailcards')) {
            const user = UserStore.getCurrent();

            const allRailcards = (await this.loadAccountRailcards(user)).toJS();

            const accountRailcards = allRailcards
                .filter(({ railcardCode, seenRailcardApproved, state }) => {
                    return state === SmartcardStatus.ACCEPTED
                        && (filterRailcards.length ? filterRailcards.includes(railcardCode) : true)
                        && (onlyUnseen ? seenRailcardApproved === false : true);
                })
                .map(({ id, railcardName }) => ({ id, railcardName }));

            if (accountRailcards.length) {
                const messageValues = accountRailcards.length === 1
                    ? { railcardName: accountRailcards[0].railcardName }
                    : { railcardCount: accountRailcards.length };

                ConfirmationModalActions.open(
                    (router) => RailcardsService.seenApprovedRailcards(user, accountRailcards.map(({ id }) => id))
                                            .then(() => {
                                                if (goToRailcardsPageOnConfirm) {
                                                    router.push(urls.location(AccountPages.ACCOUNT_PHOTOCARDS_RAILCARDS));
                                                }
                                            }),
                    accountRailcards.length === 1
                        ? 'railcard.newlyApprovedRailcards.modal.message.one'
                        : 'railcard.newlyApprovedRailcards.modal.message.multiple',
                    {
                        title: 'railcard.newlyApprovedRailcards.modal.title',
                        confirmButtonText: goToRailcardsPageOnConfirm ? 'railcard.newlyApprovedRailcards.modal.confirmButtonText' : 'button.text.confirm',
                        messageValues,
                        onCancel: () => RailcardsService.seenApprovedRailcards(user, accountRailcards.map(({ id }) => id))
                    });
            }

            const unsyncedKeyGoRailcards = allRailcards
                .filter(({ railcardCode, seenSyncKeyGoNotification, syncedWithKeygo, state }) =>
                    state === SmartcardStatus.ACCEPTED
                    && railcardCode === RAILCARD1617_CODE
                    && !seenSyncKeyGoNotification
                    && !syncedWithKeygo)
                .map(({ id, railcardName }) => ({ id, railcardName }));

            if (unsyncedKeyGoRailcards.length) {
                const setSeenSyncKeyGoNotification = () =>
                    RailcardsService.seenSyncKeyGoNotification(user, unsyncedKeyGoRailcards.map(({ id }) => id));

                ConfirmationModalActions.open(
                    router => setSeenSyncKeyGoNotification().then(() => {
                        router.push(urls.location(AccountPages.ACCOUNT_PHOTOCARDS_RAILCARDS));
                    }),
                    'railcard.syncKeyGoRailcards.modal.message',
                    {
                        title: 'railcard.syncKeyGoRailcards.modal.title',
                        confirmButtonText: 'railcard.syncKeyGoRailcards.modal.confirmButtonText',
                        messageValues: {},
                        onCancel: setSeenSyncKeyGoNotification
                    });
            }
        }
    },

    async unsubscribe (id, token, user) {
        AppDispatcher.dispatch({
            id,
            token,
            actionType: ActionTypes.UNSUBSCRIBE_STARTED
        });

        try {
            await CustomerService.unsubscribe(id, token);
            AppDispatcher.dispatch({
                actionType: ActionTypes.UNSUBSCRIBE_COMPLETE
            });

            if (user.isLoggedIn()) {
                user = await CustomerService.fetch(user.authToken, user);
                AppDispatcher.dispatch({
                    actionType: ActionTypes.USER_UPDATED,
                    user
                });
            }

        } catch (error) {
            AppDispatcher.dispatch({
                error,
                actionType: ActionTypes.UNSUBSCRIBE_FAILED
            });
            throw error;
        }
    },

    async activateSmartcard(user) {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_ACTIVATION_STARTED
        });

        try {
            await CustomerService.activateSmartcard(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_ACTIVATED
            });

        } catch (error) {
            const activationError = new SmartcardActivationError('Unexpected activation error', { error });

            AppDispatcher.dispatch({
                activationError,
                actionType: ActionTypes.SMARTCARD_ACTIVATION_FAILED
            });

            throw activationError;
        }
    },

    dismissSmartcardCTA() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_CTA_DISMISSED
        });
    },

    checkUserCookie() {
        AppDispatcher.dispatch({
            actionType: ActionTypes.CHECK_USER_COOKIE
        });
    },

    async transferSeasonTicket (user, transferForm, params) {
        await CustomerService.transferSeasonTicket(user, transferForm, params);
        await this.getSmartcard(user);
        AppDispatcher.dispatch({
            actionType: ActionTypes.SMARTCARD_PAPER_TRANSFER_COMPLETE
        });
    },

    async getAssociatedSmartcards (user, includeUserSmartcard = false, onlyActiveSmartcards = false) {
        try {
            let { smartcards, userSmartcard } = await SmartcardService.getAssociatedSmartcards(user, onlyActiveSmartcards);

            const smartcard = SmartcardStore.getCurrent();

            const hasValidSmartcard = smartcard && (smartcard.isAwaitingProduct() || smartcard.isActiveAndAvailableForMoreProducts());

            if (includeUserSmartcard && hasValidSmartcard) {
                const smartcardList = new List([
                    new SmartcardAssociationInfo({
                        associatedSmartcardNumber: smartcard.isrn,
                        cardHolderDateOfBirth: smartcard.cardHolderDateOfBirth,
                        expiryDate: smartcard.expiryDate,
                        customerName: t('delivery.smartcard-passengers.your-smartcard'),
                        customerId: user.id,
                        isAdult: user.isAdult(),
                        isMain: true, // mark as your smartcard
                        isNewCardRequestConfirmed: userSmartcard ? userSmartcard.isNewCardRequestConfirmed : false
                    }),
                ]);
                smartcards = smartcardList.concat(smartcards);
            } else if (includeUserSmartcard && !smartcard) {
                const smartcardList = new List([
                    new SmartcardAssociationInfo({
                        associatedSmartcardNumber: '',
                        customerName: t('delivery.smartcard-passengers.new-smartcard'),
                        customerId: user.id,
                        isAdult: user.isAdult(),
                        isMain: true, // mark as your smartcard
                        isNewCardRequestConfirmed: false
                    }),
                ]);
                smartcards = smartcardList.concat(smartcards);
            }

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_ASSOCIATED_SMARTCARDS_LOADED,
                smartcards
            });
        } catch (error) {
            console.warn(error);
            throw error;
        }
    },

    async getAssociatedAccounts (user, smartcard) {
        if (!smartcard) { return; }

        try {
            const accounts = await SmartcardService.getAssociatedAccounts(user);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_ASSOCIATED_ACCOUNTS_LOADED,
                accounts
            });
        } catch (error) {
            console.warn(error);
            throw error;
        }
    },

    handleAssociationErrors (error, shouldThrow) {
        const errorMessage = isErrorPresent(error) && error.body.errors[0].message.toLowerCase();
        let heading = 'smartcard.associate.error.heading';
        let title = 'smartcard.associate.error.unknown.title';
        let message = 'smartcard.associate.error.unknown.message';

        if (errorMessage) {
            const isSmartcardNotFoundForEmail = errorMessage.includes('not found for email');
            const isSmartcardNotFoundForIsrn = errorMessage.includes('not found for smartcard number');
            const isUsersSmartcard = errorMessage.includes('can\'t link their own');
            const isSmartcardAlreadyAssociated = errorMessage.includes('already linked');
            const isSmartcardInactive = errorMessage.includes('is not active');
            const isAssociationInWrongState = errorMessage.includes('link in state');
            const isUnknownCustomer = errorMessage.includes('unknown customer');
            const isSmartcardEmpty = errorMessage.includes('number is empty');

            if (isSmartcardNotFoundForEmail) {
                title = 'smartcard.associate.error.notFound.title';
                message = `smartcard.associate.error.notFound.email.message`;
            } else if (isSmartcardNotFoundForIsrn) {
                title = 'smartcard.associate.error.notFound.title';
                message = `smartcard.associate.error.notFound.isrn.message`;
            } else if (isUsersSmartcard) {
                title = 'smartcard.associate.error.invalid.title';
                message = 'smartcard.associate.error.own.message';
            } else if (isSmartcardAlreadyAssociated) {
                title = 'smartcard.associate.error.invalid.title';
                message = 'smartcard.associate.error.alreadyRequested.message';
            } else if (isSmartcardInactive) {
                title = 'smartcard.associate.error.invalid.title';
                message = 'smartcard.associate.error.inactive.message';
            } else if (isUnknownCustomer) {
                title = 'smartcard.associate.error.smartcardNotFound.title';
                message = 'smartcard.associate.error.notFound.email.message';
            } else if (isSmartcardEmpty) {
                title = 'smartcard.associate.error.smartcardNotFound.title';
                message = 'smartcard.associate.error.notFound.isrn.message';
            } else if (isAssociationInWrongState) {
                heading = 'smartcard.dissociate.error.heading';
                title = 'smartcard.associate.error.remove-association.title';
                message = errorMessage;
            }
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.MODAL_OPENED,
            type: GlobalModals.ERROR,
            data: {
                heading,
                message,
                title
            }
        });

        if (shouldThrow && !errorMessage) {
            throw error;
        }
    },

    async getSmartcardToAssociate (user, query) {
        let smartcardToAssociate = null;

        try {
            if (query && query.includes("@")) {
                smartcardToAssociate = await SmartcardService.queryAvailableAssociations(user, {
                    email: decodeURIComponent(query)
                });
            } else {
                smartcardToAssociate = await SmartcardService.queryAvailableAssociations(user, {
                    smartcardNumber: query
                });
            }

            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_TO_ASSOCIATE_LOADED,
                smartcardToAssociate
            });
        } catch (error) {
            this.handleAssociationErrors(error);
        }

        return smartcardToAssociate;
    },

    async associateSmartcard (user, query) {
        const smartcardToAssociate = await this.getSmartcardToAssociate(user, query);

        if (smartcardToAssociate && smartcardToAssociate.smartcardNumber) {
            try {
                await SmartcardService.associateSmartcard(user, smartcardToAssociate.smartcardNumber);
                AppDispatcher.dispatch({
                    actionType: ActionTypes.SMARTCARD_ASSOCIATION_COMPLETED,
                });

                AppDispatcher.dispatch({
                    actionType: ActionTypes.MODAL_OPENED,
                    type: GlobalModals.INFO,
                    data: {
                        heading: 'smartcard.associate.confirm.heading',
                        title: 'smartcard.associate.confirm.message'
                    }
                });
            } catch (error) {
                this.handleAssociationErrors(error, true);
            }
        }
    },

    async dissociateSmartcard (user, associatedSmartcard) {
        try {
            await SmartcardService.dissociateSmartcard(user, associatedSmartcard);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_ASSOCIATION_REMOVED,
            });
        } catch (error) {
            this.handleAssociationErrors(error, true);
        }
    },

    async dissociateAccount (user, associatedSmartcard) {
        try {
            await SmartcardService.dissociateAccount(user, associatedSmartcard);
            AppDispatcher.dispatch({
                actionType: ActionTypes.SMARTCARD_ASSOCIATION_REMOVED,
            });
        } catch (error) {
            this.handleAssociationErrors(error, true);
        }
    },

    async confirmAssociation (customerId, choice, token) {
        try {
            return await SmartcardService.confirmAssociation(customerId, choice, token);
        } catch (error) {
            throw error;
        }
    },

    async deleteCustomer (user, password) {
        const { email, id, authToken } = user;

        try {
            await CustomerService.auth(email, password);
            await CustomerService.deleteCustomer(id);
            const updatedUser = await CustomerService.fetch(authToken, user);

            AppDispatcher.dispatch({
                actionType: ActionTypes.USER_UPDATED,
                user: updatedUser,
            });

        } catch (error) {
            const errorMessage = isErrorPresent(error) && error.body.errors[0].message.toLowerCase();

            if (errorMessage && errorMessage.includes('customer authentication failed')) {
                throw new CustomerLoginError();
            }

            throw error;
        }
    },

    async cancelDeletionRequest (user, requestId) {
        const { id, authToken } = user;

        try {
            await CustomerService.cancelDeletionRequest(id, requestId);
            const updatedUser = await CustomerService.fetch(authToken, user);

            AppDispatcher.dispatch({
                actionType: ActionTypes.USER_UPDATED,
                user: updatedUser,
            });

        } catch (error) {
            throw error;
        }
    },

};

export default CustomerActions;
